import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import { fetchBossRankings } from "../api/rankings";
import { EQ_CLASS_NAMES, classFromSlug, classToSlug } from "../api/eqClasses";
import { fetchServerProgressionOverview } from "../api/progression";
import type {
  ParseRecordSummary,
  ProgressionBossRequirement,
  ProgressionExpansionSummary,
  ProgressionTierSummary,
} from "../api/types";
import { ErrorState } from "../components/ErrorState";
import { LoadingState } from "../components/LoadingState";
import { RankingsSectionNav } from "../components/rankings/RankingsSectionNav";
import {
  getActiveServerSlug,
  serverCharacterPath,
  serverGuildPath,
} from "../utils/siteContext";

type RankingMetric = "DAMAGE" | "HEALING";

const PAGE_SIZE = 50;

function sortedExpansions(expansions: ProgressionExpansionSummary[]) {
  return [...expansions].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );
}

function displayExpansionName(expansion: ProgressionExpansionSummary) {
  return expansion.name === "Original EverQuest" ? "Classic" : expansion.name;
}

function tiersFor(expansion: ProgressionExpansionSummary) {
  return [...(expansion.tiers ?? expansion.raidTiers ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );
}

function objectivesFor(tier: ProgressionTierSummary) {
  return [...(tier.objectives ?? tier.raidObjectives ?? [])].sort(
    (a, b) =>
      (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name),
  );
}

function bossesForTier(tier: ProgressionTierSummary) {
  const unique = new Map<string, ProgressionBossRequirement>();
  for (const objective of objectivesFor(tier)) {
    for (const boss of objective.requiredBosses ?? objective.bosses ?? []) {
      const key = String(
        bossIdValue(boss) ?? bossSlugValue(boss) ?? bossName(boss),
      );
      if (!unique.has(key)) unique.set(key, boss);
    }
  }
  return [...unique.values()];
}

function bossesForExpansion(expansion: ProgressionExpansionSummary) {
  const unique = new Map<string, ProgressionBossRequirement>();
  for (const tier of tiersFor(expansion)) {
    for (const boss of bossesForTier(tier)) {
      const key = String(
        bossIdValue(boss) ?? bossSlugValue(boss) ?? bossName(boss),
      );
      if (!unique.has(key)) unique.set(key, boss);
    }
  }
  return [...unique.values()];
}

function partitionsForExpansion(expansion: ProgressionExpansionSummary | null) {
  if (!expansion) return [];
  const partitions = new Map<
    string,
    { id: string | number; name: string; slug: string | null }
  >();
  for (const tier of tiersFor(expansion)) {
    for (const partition of tier.rankingPartitions ?? []) {
      partitions.set(String(partition.id), partition);
    }
  }
  return [...partitions.values()];
}

function partitionsFromRaidContent(raidContent: unknown) {
  const partitions = ((raidContent as { rankingPartitions?: Array<{ rankingPartitionId?: string; id?: string; name: string; slug?: string | null; isActive?: boolean }> } | null)?.rankingPartitions ?? [])
    .filter((entry) => entry.isActive !== false)
    .map((entry) => ({
      id: entry.rankingPartitionId ?? entry.id ?? "",
      name: entry.name,
      slug: entry.slug ?? null,
    }))
    .filter((entry) => entry.id);
  const deduped = new Map<string, { id: string; name: string; slug: string | null }>();
  for (const partition of partitions) deduped.set(String(partition.id), partition);
  return [...deduped.values()].sort((a, b) => a.name.localeCompare(b.name));
}

function mergePartitionOptionsFromRankings(
  results: Array<{ filterOptions?: { rankingPartitions?: Array<{ id: string | number; name: string; slug?: string | null }> } } | undefined>,
) {
  const map = new Map<string, { id: string | number; name: string; slug: string | null }>();
  for (const result of results) {
    for (const partition of result?.filterOptions?.rankingPartitions ?? []) {
      map.set(String(partition.id), {
        id: partition.id,
        name: partition.name,
        slug: partition.slug ?? null,
      });
    }
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
}

function bossIdValue(boss: ProgressionBossRequirement) {
  return (
    boss.raidBossId ?? boss.raidBoss?.id ?? boss.boss?.id ?? boss.id ?? null
  );
}

function bossSlugValue(boss: ProgressionBossRequirement) {
  return boss.raidBoss?.slug ?? boss.boss?.slug ?? boss.slug ?? null;
}

function bossName(boss: ProgressionBossRequirement) {
  return boss.raidBoss?.name ?? boss.boss?.name ?? boss.name ?? "Unknown boss";
}

function bossZone(boss: ProgressionBossRequirement) {
  return (
    boss.raidBoss?.raidZone?.name ??
    boss.boss?.raidZone?.name ??
    boss.zone ??
    null
  );
}

function slugify(value: string | number | null | undefined) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9`'._-]+/gi, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function bossPathSlug(boss: ProgressionBossRequirement) {
  return (
    bossSlugValue(boss) ??
    slugify(bossName(boss)) ??
    String(bossIdValue(boss) ?? "")
  );
}

function metricFromSegment(
  value: string | null | undefined,
  fallback: RankingMetric = "DAMAGE",
): RankingMetric {
  return value === "healing" || value === "HEALING"
    ? "HEALING"
    : value === "damage" || value === "DAMAGE"
      ? "DAMAGE"
      : fallback;
}

function metricSegment(metric: RankingMetric) {
  return metric === "HEALING" ? "healing" : "damage";
}

function classShortName(className: string | null | undefined) {
  const normalized = className?.trim();
  if (!normalized) return "???";

  const shorthand: Record<string, string> = {
    Bard: "BRD",
    Beastlord: "BST",
    Berserker: "BER",
    Cleric: "CLR",
    Druid: "DRU",
    Enchanter: "ENC",
    Magician: "MAG",
    Monk: "MNK",
    Necromancer: "NEC",
    Paladin: "PAL",
    Ranger: "RNG",
    Rogue: "ROG",
    "Shadow Knight": "SHD",
    Shaman: "SHM",
    Warrior: "WAR",
    Wizard: "WIZ",
  };

  return shorthand[normalized] ?? normalized.slice(0, 3).toUpperCase();
}

function rankBadgeClass(rank: number) {
  if (rank === 1) return "rank-badge rank-badge--gold";
  if (rank === 2) return "rank-badge rank-badge--silver";
  if (rank === 3) return "rank-badge rank-badge--bronze";
  return "rank-badge rank-badge--plain";
}

function RankBadge({ rank }: { rank: number }) {
  return <span className={rankBadgeClass(rank)}>#{rank}</span>;
}

function formatInteger(value: number | null | undefined) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "0";
  return Math.round(value).toLocaleString();
}

function formatRate(value: number | null | undefined) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "0";
  return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function formatDuration(seconds: number | null | undefined) {
  if (typeof seconds !== "number" || !Number.isFinite(seconds) || seconds <= 0)
    return "—";
  const total = Math.round(seconds);
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${String(secs).padStart(2, "0")}`;
}

function formatDate(value: string | null | undefined) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function metricAmount(parse: ParseRecordSummary, metric: RankingMetric) {
  return metric === "HEALING" ? parse.totalHealing : parse.totalDamage;
}

function petAmount(parse: ParseRecordSummary, metric: RankingMetric) {
  return metric === "HEALING" ? parse.petHealing : parse.petDamage;
}

function metricRate(parse: ParseRecordSummary, metric: RankingMetric) {
  return metric === "HEALING" ? parse.hps : parse.dps;
}

function rankingPath(
  expansionSlug: string,
  bossSlug: string | null,
  metric: RankingMetric,
  className?: string | null,
) {
  if (!bossSlug)
    return `/rankings/boss-rankings/${encodeURIComponent(expansionSlug)}?metric=${metric}`;
  const base = `/rankings/boss-rankings/${encodeURIComponent(expansionSlug)}/${encodeURIComponent(bossSlug)}/${metricSegment(metric)}`;
  const classSlug = className ? classToSlug(className) : null;
  return classSlug ? `${base}/${encodeURIComponent(classSlug)}` : base;
}

function withSearch(path: string, params: URLSearchParams) {
  const next = new URLSearchParams(params);
  next.delete("page");
  const text = next.toString();
  return text ? `${path}?${text}` : path;
}

function BossRankingRows({
  rows,
  metric,
  expansionSlug,
  bossSlug,
  startRank = 1,
}: {
  rows: ParseRecordSummary[];
  metric: RankingMetric;
  expansionSlug: string;
  bossSlug: string;
  startRank?: number;
}) {
  const serverSlug = getActiveServerSlug();
  return (
    <div className="table-responsive">
      <table className="table table-dark table-sm table-hover align-middle mb-0 rankings-mini-table boss-rankings-table">
        <thead>
          <tr>
            <th className="text-body-secondary" style={{ width: "4rem" }}>
              Rank
            </th>
            <th>Character</th>
            <th className="text-body-secondary" style={{ width: "3.25rem" }}>
              Class
            </th>
            <th>Guild</th>
            <th className="text-end">
              Total {metric === "HEALING" ? "Healing" : "Damage"}
            </th>
            <th className="text-end">Pet</th>
            <th className="text-end">{metric === "HEALING" ? "HPS" : "DPS"}</th>
            <th className="text-end">Duration</th>
            <th className="text-end">Date</th>
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows.map((parse, index) => {
              const rank = startRank + index;
              const encounterPath = `/encounters/${encodeURIComponent(parse.encounterId)}`;
              return (
                <tr key={parse.id}>
                  <td>
                    <Link to={encounterPath} className="text-decoration-none">
                      <RankBadge rank={rank} />
                    </Link>
                  </td>
                  <td>
                    <Link
                      to={serverCharacterPath(serverSlug, {
                        id: parse.characterId,
                        name: parse.characterName,
                      })}
                      className="link-light fw-semibold text-decoration-none"
                    >
                      {parse.characterName}
                    </Link>
                  </td>
                  <td>
                    <Link
                      to={withSearch(
                        rankingPath(
                          expansionSlug,
                          bossSlug === "overall" ? null : bossSlug,
                          metric,
                          parse.characterClass,
                        ),
                        new URLSearchParams(window.location.search),
                      )}
                      className="class-shorthand text-decoration-none"
                    >
                      {classShortName(parse.characterClass)}
                    </Link>
                  </td>
                  <td>
                    {parse.guild ? (
                      <Link
                        to={serverGuildPath(serverSlug, parse.guild)}
                        className="link-light text-decoration-none"
                      >
                        {parse.guild.name}
                      </Link>
                    ) : (
                      <span className="text-body-secondary">Unguilded</span>
                    )}
                  </td>
                  <td className="text-end tabular-nums">
                    <Link
                      to={encounterPath}
                      className="link-light text-decoration-none"
                    >
                      {formatInteger(metricAmount(parse, metric))}
                    </Link>
                  </td>
                  <td className="text-end tabular-nums">
                    <Link
                      to={encounterPath}
                      className="link-secondary text-decoration-none"
                    >
                      {formatInteger(petAmount(parse, metric))}
                    </Link>
                  </td>
                  <td className="text-end tabular-nums">
                    <Link
                      to={encounterPath}
                      className="link-light fw-semibold text-decoration-none"
                    >
                      {formatRate(metricRate(parse, metric))}
                    </Link>
                  </td>
                  <td className="text-end tabular-nums">
                    {formatDuration(parse.durationSec)}
                  </td>
                  <td className="text-end text-nowrap">
                    {formatDate(parse.encounter?.startedAt ?? parse.createdAt)}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={9} className="text-body-secondary py-3">
                No parses found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function BossRankingCard({
  title,
  subtitle,
  rows,
  metric,
  expansionSlug,
  bossSlug,
  allLink,
}: {
  title: string;
  subtitle?: string | null;
  rows: ParseRecordSummary[];
  metric: RankingMetric;
  expansionSlug: string;
  bossSlug: string;
  allLink?: string;
}) {
  return (
    <article className="card border-secondary-subtle bg-body-tertiary rankings-card boss-rankings-card mb-4">
      <div className="card-body">
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
          <div>
            <h2 className="h5 mb-1">{title}</h2>
            {subtitle ? (
              <div className="small text-body-secondary">{subtitle}</div>
            ) : null}
          </div>
          {allLink ? (
            <Link to={allLink} className="small link-secondary">
              All Rankings
            </Link>
          ) : null}
        </div>
        <BossRankingRows
          rows={rows.slice(0, 10)}
          metric={metric}
          expansionSlug={expansionSlug}
          bossSlug={bossSlug}
        />
      </div>
    </article>
  );
}

export function BossRankingsPage({
  metricOverride,
}: {
  metricOverride?: RankingMetric;
}) {
  const params = useParams<{
    expansionSlug?: string;
    bossSlug?: string;
    metricSegment?: string;
    classSlug?: string;
    bossId?: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const serverSlug = getActiveServerSlug();

  const progressionQuery = useQuery({
    queryKey: ["rankings", "boss-rankings", "progression", serverSlug],
    queryFn: () => fetchServerProgressionOverview(serverSlug),
  });

  const overview = progressionQuery.data?.progression ?? null;
  const server = progressionQuery.data?.server ?? null;
  const expansions = sortedExpansions(overview?.expansions ?? []);
  const expansionSlug =
    params.expansionSlug ?? expansions[0]?.slug ?? "classic";
  const selectedExpansion =
    expansions.find((expansion) => expansion.slug === expansionSlug) ??
    expansions[0] ??
    null;
  const bosses = selectedExpansion ? bossesForExpansion(selectedExpansion) : [];
  const selectedBossSlug = params.bossSlug ?? params.bossId ?? null;
  const selectedBoss = selectedBossSlug
    ? bosses.find(
        (boss) =>
          bossPathSlug(boss) === selectedBossSlug ||
          String(bossIdValue(boss)) === selectedBossSlug,
      )
    : null;
  const selectedBossId = selectedBoss ? bossIdValue(selectedBoss) : null;
  const selectedBossPathSlug = selectedBoss
    ? bossPathSlug(selectedBoss)
    : selectedBossSlug;
  const metric =
    metricOverride ??
    metricFromSegment(params.metricSegment ?? searchParams.get("metric"));
  const routeClass = classFromSlug(params.classSlug);
  const characterClass = routeClass ?? searchParams.get("class") ?? undefined;
  const rankingPartitionId =
    searchParams.get("rankingPartitionId") ?? undefined;
  const page = Math.max(
    1,
    Number.parseInt(searchParams.get("page") ?? "1", 10) || 1,
  );
  const serverId =
    typeof server?.id === "number"
      ? server.id
      : Number(server?.id) || undefined;
  const expansionPartitions = partitionsForExpansion(selectedExpansion);

  const bossQueries = useQueries({
    queries: bosses.map((boss) => {
      const id = bossIdValue(boss);
      return {
        queryKey: [
          "rankings",
          "boss-card",
          serverId,
          id,
          rankingPartitionId,
          characterClass,
          metric,
        ],
        queryFn: () =>
          fetchBossRankings(String(id), {
            serverId,
            rankingPartitionId,
            characterClass,
            metric,
            limit: 10,
          }),
        enabled: Boolean(id && serverId),
      };
    }),
  });

  const selectedBossQuery = useQuery({
    queryKey: [
      "rankings",
      "boss-all",
      serverId,
      selectedBossId,
      rankingPartitionId,
      characterClass,
      metric,
    ],
    queryFn: () =>
      fetchBossRankings(String(selectedBossId), {
        serverId,
        rankingPartitionId,
        characterClass,
        metric,
        limit: 500,
      }),
    enabled: Boolean(selectedBossId && serverId),
  });

  const raidContentPartitions = partitionsFromRaidContent(progressionQuery.data?.raidContent);
  const partitions = selectedBoss
    ? mergePartitionOptionsFromRankings([selectedBossQuery.data])
    : mergePartitionOptionsFromRankings(bossQueries.map((query) => query.data)).length > 0
      ? mergePartitionOptionsFromRankings(bossQueries.map((query) => query.data))
      : raidContentPartitions.length > 0
        ? raidContentPartitions
        : expansionPartitions;

  const rootRowsByBoss = new Map<string, ParseRecordSummary[]>();
  bosses.forEach((boss, index) => {
    const id = bossIdValue(boss);
    const rows = bossQueries[index]?.data?.rankings ?? [];
    if (id) rootRowsByBoss.set(String(id), rows);
  });

  const overallRows = [...rootRowsByBoss.values()]
    .flat()
    .sort((a, b) => metricRate(b, metric) - metricRate(a, metric))
    .slice(0, 10);

  const updateSearch = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("page");
    setSearchParams(next);
  };

  const clearFilters = () => {
    setSearchParams(
      metric === "HEALING"
        ? new URLSearchParams({ metric: "HEALING" })
        : new URLSearchParams(),
    );
  };

  const metricPath = (nextMetric: RankingMetric) => {
    if (selectedBossPathSlug) {
      return withSearch(
        rankingPath(
          expansionSlug,
          selectedBossPathSlug,
          nextMetric,
          characterClass,
        ),
        searchParams,
      );
    }
    const next = new URLSearchParams(searchParams);
    next.set("metric", nextMetric);
    next.delete("page");
    return `/rankings/boss-rankings/${encodeURIComponent(expansionSlug)}?${next.toString()}`;
  };

  const classPath = (nextClass: string | null) => {
    if (selectedBossPathSlug)
      return withSearch(
        rankingPath(expansionSlug, selectedBossPathSlug, metric, nextClass),
        searchParams,
      );
    const next = new URLSearchParams(searchParams);
    if (nextClass) next.set("class", nextClass);
    else next.delete("class");
    next.delete("page");
    return `/rankings/boss-rankings/${encodeURIComponent(expansionSlug)}${next.toString() ? `?${next.toString()}` : ""}`;
  };

  if (progressionQuery.isLoading) {
    return (
      <div className="content-wrapper py-4">
        <LoadingState label="Loading boss rankings" />
      </div>
    );
  }

  if (progressionQuery.isError) {
    return (
      <div className="content-wrapper py-4">
        <ErrorState
          title="Could not load boss rankings"
          error={progressionQuery.error}
        />
      </div>
    );
  }

  const isDetail = Boolean(selectedBoss);
  const selectedRows = selectedBossQuery.data?.rankings ?? [];
  const pageCount = Math.max(1, Math.ceil(selectedRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pagedRows = selectedRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  return (
    <>
      <section className="page-hero rankings-hero">
        <div className="content-wrapper py-4">
          <h1 className="display-6 fw-semibold mb-0">
            Boss Rankings
            <span className="page-hero__server d-block">
              {server?.name ?? "Server"}
            </span>
          </h1>
          <p className="fs-3 mb-0 text-body-secondary mt-2">
            {selectedBoss
              ? `${bossName(selectedBoss)}${bossZone(selectedBoss) ? ` · ${bossZone(selectedBoss)}` : ""}`
              : "All Boss Rankings"}
          </p>
        </div>
        {expansions.length > 0 && selectedExpansion ? (
          <div className="page-subnav rankings-expansion-subnav">
            <div className="content-wrapper">
              <ul
                className="nav secondary-tabs"
                aria-label="Expansion rankings"
              >
                {expansions.map((expansion) => (
                  <li className="nav-item" key={expansion.id}>
                    <Link
                      className={`nav-link ${selectedExpansion.slug === expansion.slug ? "active" : ""}`}
                      to={`/rankings/boss-rankings/${encodeURIComponent(expansion.slug)}${metric === "HEALING" ? "?metric=HEALING" : ""}`}
                    >
                      {displayExpansionName(expansion)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}
        <RankingsSectionNav
          activeSection="bosses"
          serverSlug={serverSlug}
          progressionExpansionSlug={selectedExpansion?.slug ?? expansionSlug}
        />
      </section>

      <div className="page-main">
        <div className="content-wrapper">
          <section
            className="resource-grid-controls"
            aria-label="Boss ranking filters"
          >
            <div className="resource-grid-controls__header">
              <div>
                <h3>Filters</h3>
              </div>
              {rankingPartitionId || characterClass || metric === "HEALING" ? (
                <button
                  className="btn btn-sm btn-outline-light"
                  type="button"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              ) : null}
            </div>
            <div className="resource-grid-controls__fields rankings-filter-grid">
              <label className="resource-grid-control">
                <span>Partition</span>
                <select
                  className="form-select form-select-sm"
                  value={rankingPartitionId ?? ""}
                  onChange={(event) =>
                    updateSearch("rankingPartitionId", event.target.value)
                  }
                >
                  <option value="">All partitions</option>
                  {partitions.map((partition) => (
                    <option
                      key={String(partition.id)}
                      value={String(partition.id)}
                    >
                      {partition.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="resource-grid-control">
                <span>Class</span>
                <select
                  className="form-select form-select-sm"
                  value={characterClass ?? ""}
                  onChange={(event) =>
                    navigate(classPath(event.target.value || null))
                  }
                >
                  <option value="">All classes</option>
                  {EQ_CLASS_NAMES.map((className) => (
                    <option key={className} value={className}>
                      {className}
                    </option>
                  ))}
                </select>
              </label>
              <label className="resource-grid-control">
                <span>Metric</span>
                <select
                  className="form-select form-select-sm"
                  value={metric}
                  onChange={(event) =>
                    navigate(metricPath(event.target.value as RankingMetric))
                  }
                >
                  <option value="DAMAGE">DPS</option>
                  <option value="HEALING">Healing</option>
                </select>
              </label>
            </div>
          </section>

          {!selectedExpansion ? (
            <div className="alert alert-secondary">
              No active expansion is configured for boss rankings.
            </div>
          ) : isDetail && selectedBoss ? (
            <section>
              <div className="d-flex flex-wrap justify-content-between gap-3 align-items-center mb-3">
                <div>
                  <h2 className="h4 mb-1">
                    {bossName(selectedBoss)}{" "}
                    {metric === "HEALING" ? "Healing" : "Damage"} Parses
                  </h2>
                </div>
                {selectedBossQuery.isFetching ? (
                  <span className="small text-body-secondary">
                    Refreshing...
                  </span>
                ) : null}
              </div>
              {selectedBossQuery.isLoading ? (
                <LoadingState label="Loading parses" />
              ) : null}
              {selectedBossQuery.isError ? (
                <ErrorState
                  title="Could not load parses"
                  error={selectedBossQuery.error}
                />
              ) : null}
              {!selectedBossQuery.isLoading && !selectedBossQuery.isError ? (
                <>
                  <div className="card border-secondary-subtle bg-body-tertiary rankings-card rankings-table-card">
                    <div className="card-body">
                      <BossRankingRows
                        rows={pagedRows}
                        metric={metric}
                        expansionSlug={expansionSlug}
                        bossSlug={
                          selectedBossPathSlug ?? bossPathSlug(selectedBoss)
                        }
                        startRank={(currentPage - 1) * PAGE_SIZE + 1}
                      />
                    </div>
                  </div>
                  {pageCount > 1 ? (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <Link
                        className={`btn btn-outline-light btn-sm ${currentPage <= 1 ? "disabled" : ""}`}
                        to={`?${new URLSearchParams({ ...Object.fromEntries(searchParams), page: String(currentPage - 1) }).toString()}`}
                      >
                        Previous
                      </Link>
                      <span className="small text-body-secondary">
                        Page {currentPage} of {pageCount}
                      </span>
                      <Link
                        className={`btn btn-outline-light btn-sm ${currentPage >= pageCount ? "disabled" : ""}`}
                        to={`?${new URLSearchParams({ ...Object.fromEntries(searchParams), page: String(currentPage + 1) }).toString()}`}
                      >
                        Next
                      </Link>
                    </div>
                  ) : null}
                </>
              ) : null}
            </section>
          ) : (
            <section>
              {bossQueries.some((query) => query.isLoading) ? (
                <div className="alert alert-secondary py-2">
                  Loading boss ranking cards...
                </div>
              ) : null}
              <BossRankingCard
                title="Overall"
                subtitle={`${displayExpansionName(selectedExpansion)} ${metric === "HEALING" ? "healing" : "damage"} parses across all configured bosses`}
                rows={overallRows}
                metric={metric}
                expansionSlug={expansionSlug}
                bossSlug={bosses[0] ? bossPathSlug(bosses[0]) : "overall"}
              />
              {bosses.map((boss, index) => {
                const id = bossIdValue(boss);
                const pathSlug = bossPathSlug(boss);
                return (
                  <BossRankingCard
                    key={String(id ?? pathSlug)}
                    title={bossName(boss)}
                    subtitle={bossZone(boss)}
                    rows={rootRowsByBoss.get(String(id)) ?? []}
                    metric={metric}
                    expansionSlug={expansionSlug}
                    bossSlug={pathSlug}
                    allLink={withSearch(
                      rankingPath(
                        expansionSlug,
                        pathSlug,
                        metric,
                        characterClass,
                      ),
                      searchParams,
                    )}
                  />
                );
              })}
              {bosses.length === 0 ? (
                <div className="alert alert-secondary">
                  No bosses are configured for this expansion.
                </div>
              ) : null}
            </section>
          )}
        </div>
      </div>
    </>
  );
}
